import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const field = "rounded-xl bg-[#FDFBF7] border-[#103080]/20 h-11";
const EMPTY = { name: "", email: "", role: "member", password: "" };

export default function MemberDialog({ state, onClose, onDone, onReset }) {
  const [form, setForm] = useState(EMPTY);
  const reset = state?.mode === "reset";

  useEffect(() => {
    if (state) setForm(EMPTY);
  }, [state]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (reset) {
      await onReset(state.member, { password: form.password });
      onClose();
      return;
    }
    try {
      await api.post("/team", form);
      toast.success(`${form.name} can now sign in`);
      onClose();
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={Boolean(state)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl bg-[#FDFBF7] border-[#103080]/10 sm:max-w-md" data-testid="member-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-brand text-2xl text-[#103080]">{reset ? `Reset password for ${state.member.name}` : "Add team member"}</DialogTitle>
          <DialogDescription>Share the temporary password privately. They can change it in Settings.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!reset && (
            <>
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input required value={form.name} onChange={set("name")} className={field} data-testid="member-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Work email</Label>
                <Input type="email" required value={form.email} onChange={set("email")} className={field} data-testid="member-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={set("role")}>
                  <SelectTrigger className={field} data-testid="member-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member" data-testid="member-role-member">Team member</SelectItem>
                    <SelectItem value="admin" data-testid="member-role-admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <Label>{reset ? "New password" : "Temporary password"} (min 8 characters)</Label>
            <Input type="text" required minLength={8} value={form.password} onChange={set("password")} className={field} data-testid="member-password-input" />
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" data-testid="member-save-button"
              className="rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200">
              {reset ? "Reset password" : "Add member"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
